import React from 'react';
import { AnimatePresence, motion, Reorder } from 'framer-motion';
import { ArrowDown, ArrowUp, GripVertical, Layers, Share2, Sparkles, X } from 'lucide-react';
import { SelectDropdown } from '../SelectDropdown';
import { AudienceLevel, LEVEL_LABELS } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
import { PathwayButton } from './PathwayButton';
interface PathwayTrayProps {
  name: string;
  /** Prospect or company this pathway is for — appears on the PDF cover. */
  company: string;
  /** True when the company shown was picked up from the conversation notes. */
  companyFromNotes: boolean;
  /** Module ids in the order the prospect should take them. */
  items: string[];
  audience: AudienceLevel | 'any';
  /** Set once the rep has chosen how edits to a saved pathway should land. */
  editContext: string | null;
  onRevert: () => void;
  onNameChange: (name: string) => void;
  onCompanyChange: (company: string) => void;
  onAudienceChange: (audience: AudienceLevel | 'any') => void;
  onCohortChange: (cohortId: string) => void;
  onRemove: (moduleId: string) => void;
  onMove: (moduleId: string, direction: -1 | 1) => void;
  onReorder: (moduleIds: string[]) => void;
  onClear: () => void;
  onShare: () => void;
}
export function PathwayTray({
  name,
  company,
  companyFromNotes,
  items,
  audience,
  editContext,
  onRevert,
  onNameChange,
  onCompanyChange,
  onAudienceChange,
  onCohortChange,
  onRemove,
  onMove,
  onReorder,
  onClear,
  onShare
}: PathwayTrayProps) {
  // Company and pathway name both appear on the document a prospect reads, so
  // neither can be missing by the time it is generated.
  const missing: string[] = [];
  if (items.length === 0) missing.push('at least one module');
  if (!company.trim()) missing.push('a company');
  if (!name.trim()) missing.push('a pathway name');
  const canSend = missing.length === 0;
  const blockedReason = canSend ? null : `Add ${missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`} to send this pathway.`;
  return <aside aria-label="Your pathway" className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm lg:sticky lg:top-6" data-id="element-1764" data-mp="mi93c8is1u14">
      <AnimatePresence initial={false} data-id="element-1765" data-mp="rf91lx9dxsho">
        {editContext && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden bg-[#30718D]/5" data-id="element-1766" data-mp="wg6rkw0mzqtq">
            <div className="flex items-start gap-2 px-5 py-3" data-id="element-1767" data-mp="1a7bkzgdevmvm">
              <p className="flex-1 text-xs font-medium text-[#30718D]" data-id="element-1768" data-mp="1f7zb7zttfzos">
                {editContext}
              </p>
              <button type="button" onClick={onRevert} className="flex-shrink-0 text-xs font-medium text-gray-500 transition-colors hover:text-gray-900" data-id="element-1769" data-mp="1k72hfnlj8byc">
                Revert
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="border-b border-gray-100 p-5" data-id="element-1770" data-mp="2uryua9etitlg">
        <div className="flex items-center justify-between" data-id="element-1771" data-mp="2ps43s6yyg60x">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-1772" data-mp="3net9orws32s2">Your pathway</h2>
          {items.length > 0 && <button type="button" onClick={onClear} className="text-xs font-medium text-gray-500 transition-colors hover:text-[#DB3725]" data-id="element-1773" data-mp="3if6nhsyz2c74">
              Clear
            </button>}
        </div>
        <p className="mt-1 text-sm text-gray-600" data-id="element-1774" data-mp="38pn6ksutksew">
          {items.length === 0 ? 'Nothing added yet' : `${items.length} ${items.length === 1 ? 'module' : 'modules'} · ${formatHours(totalHours(items))} total`}
        </p>
      </div>

      <div className="space-y-3 border-b border-gray-100 p-5" data-id="element-1775" data-mp="3lv9k2nt5c5ae">
        <label className="block" data-id="element-1776" data-mp="3qvxjnxvfxug8">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1777" data-mp="3qwyx28zxit14">
            Company
            <span aria-hidden="true" className="ml-0.5 text-[#DB3725]" data-id="element-1778" data-mp="3c0rfzhjti7q2">
              *
            </span>
          </span>
          <input value={company} onChange={e => onCompanyChange(e.target.value)} placeholder="e.g. Meridian Health" required aria-required="true" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1779" data-mp="8hy0v0be5ats" />
          {companyFromNotes && <span className="mt-1 flex items-center gap-1 text-xs text-[#30718D]" data-id="element-1780" data-mp="refu5m5w1is5">
              <Sparkles size={11} aria-hidden="true" data-id="element-1781" data-mp="w8k0c37bxms6" />
              From your notes — edit if that’s not right
            </span>}
        </label>

        <label className="block" data-id="element-1782" data-mp="2bz2z6309i3c6">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1783" data-mp="2by1ow0r2lkvi">
            Pathway name
            <span aria-hidden="true" className="ml-0.5 text-[#DB3725]" data-id="element-1784" data-mp="2qpabs23l8s4y">
              *
            </span>
          </span>
          <input value={name} onChange={e => onNameChange(e.target.value)} placeholder="e.g. New Manager Track" required aria-required="true" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 placeholder:text-gray-500 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1785" data-mp="1xyju94r3fde8" />
        </label>

        <div data-id="element-1786" data-mp="3idc7bk1xls31">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1787" data-mp="3icu0jthn7vgv">
            Built for
          </span>
          <SelectDropdown ariaLabel="Audience this pathway is built for" fullWidth scrollMenu value={audience} onChange={value => onAudienceChange(value as AudienceLevel | 'any')} options={[{
          value: 'any',
          label: 'Mixed audience'
        }, ...(Object.keys(LEVEL_LABELS) as AudienceLevel[]).map(level => ({
          value: level,
          label: LEVEL_LABELS[level]
        }))]} data-id="element-1788" data-mp="2b2ebrjmc8ksb" />
        </div>

      </div>

      <div className="max-h-[420px] overflow-y-auto p-5" data-id="element-1789" data-mp="21c2wj0x7ep64">
        {items.length === 0 ? <div className="py-8 text-center" data-id="element-1790" data-mp="263l0zyyfr7vu">
            <Layers size={24} className="mx-auto text-gray-400" data-id="element-1791" data-mp="264ckwcio7h6a" />
            <p className="mx-auto mt-3 max-w-[15rem] text-sm text-gray-600" data-id="element-1792" data-mp="19k3v3z1amu63">
              Add modules from the results, then drag them into the order the
              prospect should take them.
            </p>
          </div> : <>
            <p className="mb-3 text-xs text-gray-500" data-id="element-1793" data-mp="160v7kbi7u8y4">
              Drag a module card to reorder it. The arrow buttons remain available
              for keyboard and precise adjustments.
            </p>
            <Reorder.Group as="ul" axis="y" values={items} onReorder={onReorder} className="space-y-2" aria-label="Modules in pathway order" data-id="element-1794" data-mp="s3gft04hpp2z">
            <AnimatePresence initial={false} data-id="element-1795" data-mp="ides49g1u8ol">
              {items.map((moduleId, index) => {
              const module = moduleById(moduleId);
              if (!module) return null;
              return <Reorder.Item key={moduleId} as="li" value={moduleId} initial={{
                opacity: 0,
                y: -6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                x: 8
              }} transition={{
                duration: 0.18,
                ease: [0.23, 1, 0.32, 1]
              }} className="flex cursor-grab items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 active:cursor-grabbing" data-id="element-1796" data-mp="9e05xm6x0k9w" data-mpi={index}>
                    <span className="mt-0.5 flex h-5 w-3 flex-shrink-0 items-center justify-center text-gray-400" aria-hidden="true" data-id="element-1797" data-mp="1j93z8oxxuzkt">
                      <GripVertical size={15} data-id="element-1798" data-mp="19bz7m9lbqbwm" />
                    </span>
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-bold text-gray-600" data-id="element-1799" data-mp="vkavi6b2rnfn">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-1800" data-mp="lllfyk6nif2k">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-1801" data-mp="lmwqr3yikn36">
                        {module.name}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500" data-id="element-1802" data-mp="6z6bikcawz8q">
                        {formatHours(module.durationHours)} · {module.category}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center" data-id="element-1803" data-mp="gxcbuxiglxbh">
                      <button type="button" onPointerDown={event => event.stopPropagation()} onClick={() => onMove(moduleId, -1)} disabled={index === 0} aria-label={`Move ${module.name} earlier`} className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-1804" data-mp="gvj39coad30u">
                        <ArrowUp size={14} data-id="element-1805" data-mp="urd2f3z9ce0r" />
                      </button>
                      <button type="button" onPointerDown={event => event.stopPropagation()} onClick={() => onMove(moduleId, 1)} disabled={index === items.length - 1} aria-label={`Move ${module.name} later`} className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-1806" data-mp="18p1izzmakz00">
                        <ArrowDown size={14} data-id="element-1807" data-mp="1vxbn0g4bxhrm" />
                      </button>
                      <button type="button" onPointerDown={event => event.stopPropagation()} onClick={() => onRemove(moduleId)} aria-label={`Remove ${module.name}`} className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-[#DB3725]" data-id="element-1808" data-mp="2tm58ig9ez1yv">
                        <X size={14} data-id="element-1809" data-mp="187ha5yk0ft7d" />
                      </button>
                    </div>
                  </Reorder.Item>;
            })}
            </AnimatePresence>
            </Reorder.Group>
          </>}
      </div>

      <div className="border-t border-gray-100 p-5" data-id="element-1810" data-mp="3jw5nr12p88xk">
        <PathwayButton fullWidth onClick={onShare} disabled={!canSend} data-id="element-1811" data-mp="3juulj5owj98k">
          <Share2 size={18} data-id="element-1812" data-mp="3ov8kly8iz8ug" />
          Review and Send
        </PathwayButton>
        <p className="mt-2 text-center text-xs text-gray-500" data-id="element-1813" data-mp="3tn0eahmz9uot">
          {blockedReason ?? 'Creates a branded PDF and a shareable link.'}
        </p>
      </div>
    </aside>;
}